/**
 * ScoreAdjustModal — modal para o master ajustar pontuação de um aluno.
 *
 * Campos: delta (int, pode ser negativo) + reason (texto curto).
 * Defaults sugeridos: +5 / -5 / texto livre.
 */

import { useEffect, useState } from 'react'

import { useAdapter } from '../adapter'

interface Props {
  membershipId: string
  displayName: string | null
  currentTotal: number
  onClose: () => void
}

const QUICK_DELTAS = [-5, -1, +1, +5, +10]

export function ScoreAdjustModal({ membershipId, displayName, currentTotal, onClose }: Props) {
  const adapter = useAdapter()
  const [delta, setDelta] = useState<number>(5)
  const [reason, setReason] = useState<string>('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function submit() {
    if (!delta || delta === 0) return
    adapter.adjustScore({
      membershipId,
      delta,
      reason: reason.trim() || 'ajuste manual',
    })
    onClose()
  }

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        background: 'rgba(0,0,0,0.45)',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(400px, 100%)',
          background: '#FFFFFF',
          color: '#0F1115',
          border: '1px solid var(--color-lab-rule, #D8D5CB)',
          borderRadius: 'var(--radius-lab, 12px)',
          boxShadow: 'var(--lab-shadow-lg, 0 20px 60px rgba(15,17,21,0.12))',
          padding: 20,
          fontFamily: 'var(--font-lab-sans, system-ui, sans-serif)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-xs, 0.75rem)',
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'var(--color-lab-ink-3, #555B66)',
            marginBottom: 4,
            fontWeight: 700,
          }}
        >
          Ajustar pontuação
        </div>
        <h2 style={{ margin: 0, fontSize: 'var(--text-lab-lg)', lineHeight: 1.2 }}>
          {displayName ?? 'Aluno anônimo'}
        </h2>
        <p
          style={{
            margin: '4px 0 16px',
            color: 'var(--color-lab-ink-3)',
            fontSize: 'var(--text-lab-sm)',
            fontFamily: 'var(--font-lab-mono)',
          }}
        >
          Total atual: <strong>{currentTotal}</strong> XP
        </p>

        <label
          style={{
            display: 'block',
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-xs)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'var(--color-lab-ink-3)',
            marginBottom: 6,
          }}
        >
          Delta
        </label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {QUICK_DELTAS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDelta(d)}
              style={{
                minWidth: 44,
                minHeight: 36,
                padding: '6px 10px',
                background: delta === d ? 'var(--color-lab-accent, #5B2DB8)' : '#F2F2ED',
                color: delta === d ? 'white' : '#0F1115',
                border: `1px solid ${delta === d ? 'var(--color-lab-accent)' : 'var(--color-lab-rule, #D8D5CB)'}`,
                borderRadius: 'var(--radius-lab-sm, 6px)',
                fontFamily: 'var(--font-lab-mono)',
                fontSize: 'var(--text-lab-sm)',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {d > 0 ? `+${d}` : d}
            </button>
          ))}
        </div>
        <input
          type="number"
          value={delta}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10)
            if (!Number.isNaN(v)) setDelta(v)
            else setDelta(0)
          }}
          style={{
            width: '100%',
            padding: '8px 10px',
            marginBottom: 16,
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-base)',
            border: '1px solid var(--color-lab-rule, #D8D5CB)',
            borderRadius: 'var(--radius-lab-sm, 6px)',
            background: '#FAFAF7',
          }}
          placeholder="delta (int, use negativo p/ subtrair)"
        />

        <label
          style={{
            display: 'block',
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-xs)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'var(--color-lab-ink-3)',
            marginBottom: 6,
          }}
        >
          Motivo (opcional)
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          placeholder="ex.: participação oral, ajuda colega..."
          style={{
            width: '100%',
            padding: '8px 10px',
            marginBottom: 16,
            fontFamily: 'var(--font-lab-sans)',
            fontSize: 'var(--text-lab-base)',
            border: '1px solid var(--color-lab-rule, #D8D5CB)',
            borderRadius: 'var(--radius-lab-sm, 6px)',
            background: '#FAFAF7',
          }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              minHeight: 40,
              padding: '8px 14px',
              background: 'transparent',
              color: 'var(--color-lab-ink)',
              border: '1px solid var(--color-lab-rule, #D8D5CB)',
              borderRadius: 'var(--radius-lab-sm, 6px)',
              fontFamily: 'var(--font-lab-mono)',
              fontSize: 'var(--text-lab-sm)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!delta}
            style={{
              minHeight: 40,
              padding: '8px 16px',
              background: 'var(--color-lab-accent, #5B2DB8)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-lab-sm, 6px)',
              fontFamily: 'var(--font-lab-mono)',
              fontSize: 'var(--text-lab-sm)',
              fontWeight: 700,
              cursor: delta ? 'pointer' : 'not-allowed',
              opacity: delta ? 1 : 0.5,
            }}
          >
            aplicar
          </button>
        </div>
      </div>
    </div>
  )
}
